import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Alert, 
  CircularProgress, 
  Container,
  Grid,
  Divider,
  Card,
  CardContent,
  CardMedia
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import LockIcon from '@mui/icons-material/Lock';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const CustomerLoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { clientLogin, customerLogin } = useAuth();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      // Try both login methods
      const success = await customerLogin(formData.email, formData.password);
      
      if (success) {
        navigate('/customer-dashboard');
      } else {
        setError('Invalid email or password. Please try again.');
      }
    } catch (error) {
      setError(error.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      <Grid container spacing={4} alignItems="center">
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ border: 'none', boxShadow: 'none' }}>
            <CardContent sx={{ p: 0 }}>
              <Typography variant="h4" gutterBottom color="primary">
                Plantation Management Portal
              </Typography>
              <Typography variant="h6" gutterBottom color="textSecondary">
                Access your plantation data and analytics
              </Typography>
              <Box sx={{ my: 3 }}>
                <Typography variant="body1" paragraph>
                  As a registered client, you can:
                </Typography>
                <ul>
                  <li>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      Monitor your plantation's performance in real-time
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      Track sex ratio data and trends
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      View detailed analytics by plantation type
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      Receive recommendations for optimizing yield
                    </Typography>
                  </li>
                </ul>
              </Box>
            </CardContent>
            <CardMedia
              component="img"
              alt="Palm Oil Plantation"
              height="280"
              image="https://images.unsplash.com/photo-1594038302926-20117c60e276?q=80&w=2000&auto=format&fit=crop"
              sx={{ borderRadius: 2, mt: 2 }}
            />
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
            <Typography variant="h5" gutterBottom align="center">
              Client Login
            </Typography>
            
            {error && (
              <Alert severity="error" sx={{ my: 2 }}>
                {error}
              </Alert>
            )}
            
            <Box component="form" onSubmit={handleLogin} sx={{ mt: 2 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={formData.email}
                onChange={handleChange}
              />
              
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleChange}
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2, py: 1.5 }}
                disabled={loading}
                startIcon={<BusinessIcon />}
              >
                {loading ? <CircularProgress size={24} /> : 'Log In'}
              </Button>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 2 }}>
                For demo: Use the email addresses of registered clients with password "client123"
              </Typography>
              
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  Not a client yet? Contact your Ranko Tracker representative to set up your account.
                </Typography>
                <Button
                  component={Link}
                  to="/"
                  variant="text"
                  color="primary"
                  sx={{ mt: 1 }}
                >
                  Back to Home
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CustomerLoginPage; 